/**
 * This software is a modified vertion of the converter.ts from `web3.js` library.
 * The original license is: [MIT](https://github.com/web3/web3.js/blob/4.x/LICENSE)
 * The original source code is: [converter.ts](https://github.com/web3/web3.js/blob/4.x/packages/web3-utils/src/converters.ts)
 */
import { isValidDecimal, isValidFloat, isValidInt } from "./validators";
//TODO: remove the necessity of using bignumber.js by parsing the scientific notation
import BigNumber from "bignumber.js";

const BN_10 = BigInt(10);

// skipcq: JS-0327
export class TokenAmountConverter {
    /**
     * Converts a BigNumber representation (fixed) to a standard representation (float).
     * @example 100 with 2 decimals -> 1.00
     */
    static formatToken(number: string, decimals: number): string {
        const value = TokenAmountConverter.parseNumber(number);
        TokenAmountConverter.validateInt(value);
        TokenAmountConverter.validateDecimals(decimals);

        if (decimals === 0 || number === "0") return value;

        // In case the number is lower than zero, we need to pad it with zeros
        // 13456789 -> 13456789, 1234 -> 001234
        const zeroPaddedValue = value.padStart(decimals, "0");

        // Get The integer part, if the number is lower than zero, it will be empty
        // 13456789 -> '13'
        // 001234 -> ''
        const integer = zeroPaddedValue.slice(0, -decimals);

        // Get the fraction part of value by counting number of zeros backward
        // 13456789 -> '456789'
        // 001234 -> '001234'
        const fraction = TokenAmountConverter.removeTrailingZeros(zeroPaddedValue.slice(-decimals));

        if (integer === "") return `0.${fraction}`;

        if (fraction === "") return integer;

        return `${integer}.${fraction}`;
    }

    /**
     * Takes a number of a unit and converts it to big int representation.
     * @example 10.0223 with 4 decimals -> 100223
     */
    static parseToken(number: string, precision: number): string {
        //Parse number in case it's in scientific notation
        const cleanedValue = TokenAmountConverter.parseNumber(number);
        TokenAmountConverter.validateNumber(cleanedValue);
        TokenAmountConverter.validateDecimals(precision);

        const [integer, decimals] = cleanedValue.split(".");

        if (precision === 0) return integer.toString();

        if (decimals) {
            // Remove trailing zeros
            // 1200 -> 12
            const numberDecimals = TokenAmountConverter.removeTrailingZeros(decimals).length;

            // Get only the decimals that we need
            // 123 with 2 decimals: Math.min(3, 2) = 2 -> 123 becomes 12
            const finalDecimals = decimals.slice(0, Math.min(numberDecimals, precision));

            const tempAmount = `${integer}${finalDecimals}`;

            //We need to add zeros in case the number is not long enough
            // 12 with 4 decimals -> 1200.
            const finalNumOfDecimals = precision - finalDecimals.length;
            const factor = TokenAmountConverter.base10IntExpo(finalNumOfDecimals);
            return TokenAmountConverter.removeLeftZeros((BigInt(tempAmount) * factor).toString());
        } else {
            const factor = TokenAmountConverter.base10IntExpo(precision);
            return (BigInt(integer) * factor).toString();
        }
    }

    static validateInt(value: string): void {
        if (!isValidInt(value)) {
            throw new Error(`Invalid integer ${value}`);
        }
    }

    static validateFloat(value: string): void {
        if (!isValidFloat(value)) {
            throw new Error(`Invalid float ${value}`);
        }
    }

    static validateNumber(value: string): void {
        if (value.includes(".")) return TokenAmountConverter.validateFloat(value);
        else return TokenAmountConverter.validateInt(value);
    }

    static validateDecimals(value: number): void {
        if (!isValidDecimal(value)) {
            throw new Error(`Invalid decimals ${value}`);
        }
    }

    static removeTrailingZeros(value: string): string {
        let position = value.length - 1;

        while (position >= 0 && value[position] === "0") {
            position--;
        }

        return value.substring(0, position + 1);
    }

    static removeLeftZeros(value: string): string {
        return value.replace(/^0+/, "");
    }

    static base10IntExpo(expo: number): bigint {
        return BN_10 ** BigInt(expo);
    }

    static parseNumber(value: string): string {
        return BigNumber(value).toFixed();
    }

    static parseBigNumber(value: string): string {
        return BigNumber(value).toFixed();
    }
}
